/**
 * Contrato de eventos del cliente de Marker (T018; implementation-notes 003 §4).
 *
 * `src/api/client.ts` emite estos eventos sobre `window` cuando la API responde con los códigos de
 * `contracts/terminal-api.md`; `routes/__root.tsx` los escucha y navega. Ningún otro módulo debe
 * usar los literales: siempre `FERMIN_EVENTS`.
 *
 * | evento                    | origen (código de la API)                       | reacción del root                  |
 * |---------------------------|-------------------------------------------------|------------------------------------|
 * | `terminalUnauthenticated` | 401 `UNAUTHENTICATED` (token desconocido)       | credencial ya borrada ⇒ `/activar` |
 * | `terminalRevoked`         | 401 `TERMINAL_SESSION_REVOKED` (`detail.reason`)| credencial ya borrada ⇒ pantalla según `reason` (US4) |
 *
 * El cierre `4401` del canal WS y el mensaje `revoked` NO pasan por aquí: los procesa la máquina
 * del canal (`channel/connection.ts`) y el reducer (`features/display/marker-state.ts`).
 */
import type { ErrorCode, TerminalRevokeReason } from '@fermin/contracts';

export const FERMIN_EVENTS = {
  terminalUnauthenticated: 'fermin:terminal-unauthenticated',
  terminalRevoked: 'fermin:terminal-revoked',
} as const;

export type FerminEventKey = keyof typeof FERMIN_EVENTS;
export type FerminEventName = (typeof FERMIN_EVENTS)[FerminEventKey];

/** Detalle del evento: el sobre de error que lo originó (nunca tokens ni cabeceras). */
export interface FerminEventDetail {
  code?: ErrorCode;
  message?: string;
  status?: number;
  requestId?: string;
  /** Solo en `terminalRevoked`: `details.reason` del `401 TERMINAL_SESSION_REVOKED`. */
  reason?: TerminalRevokeReason;
}

export type FerminEventHandler = (
  detail: FerminEventDetail | undefined,
  event: CustomEvent<FerminEventDetail | undefined>,
) => void;

/** Emite el evento sobre `window`. No hace nada fuera del navegador. */
export function emitFerminEvent(name: FerminEventName, detail?: FerminEventDetail): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent<FerminEventDetail | undefined>(name, { detail }));
}

/** Suscribe un manejador y devuelve la función para desuscribirse. */
export function onFerminEvent(name: FerminEventName, handler: FerminEventHandler): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const listener = (event: Event) => {
    const custom = event as CustomEvent<FerminEventDetail | undefined>;
    handler(custom.detail, custom);
  };
  window.addEventListener(name, listener);
  return () => window.removeEventListener(name, listener);
}
